"use client";

import { routes } from "@/data/routes";
import { cn } from "@/lib/components";
import QuotesSvg from "@/svg/quotes.svg";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import Link from "next/link";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../base/carousel";
import data from "./Home.data";

export function Home() {
  return (
    <div className="flex flex-col items-center gap-y-8">
      <section className="w-full">
        <Link href={routes.products.path}>
          {/* Mobile image */}
          <Image
            src={data.spotlight.mobileImage}
            alt="spotlight"
            width={768}
            height={1024}
            className={cn("block md:hidden", "h-auto w-full")}
            priority
          />
          {/* Desktop image */}
          <Image
            src={data.spotlight.desktopImage}
            alt="spotlight"
            width={1920}
            height={900}
            className={cn("hidden md:block", "h-auto w-full")}
            priority
          />
        </Link>
      </section>
      <HomeSection className="flex flex-col gap-6">
        <h2 className="text-2xl">{data.categories.title}</h2>
        <Carousel
          opts={{
            loop: true,
            align: "center",
            active: true,
          }}
          plugins={[
            Autoplay({
              delay: 2000,
            }),
          ]}
          className="w-full"
        >
          <CarouselContent>
            {data.categories.carousel.map((route, ridx) => (
              <CarouselItem key={route.label + ridx} className="h-56 basis-42">
                <Link href={route.path}>
                  <Image src={route.carouselImage} alt={route.label} width={191} height={224} />
                  <div className="mt-2 w-full text-center uppercase">{route.label}</div>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-2" />
          <CarouselNext className="right-2" />
        </Carousel>
      </HomeSection>

      {data.sections.map((section) => (
        <HomeSection key={section.title.firstPart + section.title.secondPart}>
          <Link href={section.path} className="relative hover:opacity-80">
            {section.type === "full" ? (
              <>
                <span className="absolute top-8 left-10 flex gap-4 text-2xl text-white md:text-4xl lg:text-6xl">
                  <p>{section.title.firstPart}</p>
                  <p className="font-didone">{section.title.secondPart}</p>
                </span>
                <Image
                  src={section.image}
                  alt={section.title.firstPart + section.title.secondPart}
                  width={1024}
                  height={446}
                  className="h-auto w-full"
                />
              </>
            ) : (
              <div
                className={cn("flex flex-wrap items-stretch", {
                  "flex-row-reverse": section.type === "split-reverse",
                })}
              >
                <Image
                  src={section.image}
                  alt={section.title.firstPart + section.title.secondPart}
                  width={512}
                  height={446}
                  className="h-auto w-full flex-1"
                />
                <div
                  className={cn(
                    "min-w-54 flex-1 bg-[rgb(247,247,247)] p-4 text-4xl",
                    "flex flex-col items-center justify-center gap-4",
                  )}
                >
                  <p>{section.title.firstPart}</p>
                  <p className="font-didone">{section.title.secondPart}</p>
                </div>
              </div>
            )}
          </Link>
        </HomeSection>
      ))}

      <HomeSection className="flex justify-center py-8">
        <div className="relative w-fit">
          <QuotesSvg className="absolute -top-10 -z-10" />
          <p className="max-w-2xl text-center text-2xl md:text-4xl">{data.quote.text}</p>
        </div>
      </HomeSection>

      <div className="w-full bg-[rgb(247,247,247)]">
        <HomeSection
          className={cn(
            "grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-10",
            "mx-auto mb-0 p-4 md:p-8",
          )}
        >
          {data.services.map((service) => (
            <HomeServiceCard
              key={service.title}
              className="flex flex-col items-center justify-center gap-4"
            >
              <service.icon className="size-10 lg:size-16" />
              <p className="text-base lg:text-xl">{service.title}</p>
            </HomeServiceCard>
          ))}
        </HomeSection>
      </div>
    </div>
  );
}

export function HomeServiceCard({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "border-border h-50 w-full border bg-white xl:h-80",
        "flex items-center justify-center",
        className,
      )}
      {...props}
    />
  );
}

export function HomeSection({ className, ...props }: React.ComponentProps<"section">) {
  return (
    <section
      className={cn("mb-8 w-full max-w-[1440px] px-4 md:px-16 xl:px-8", className)}
      {...props}
    />
  );
}
